import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { LoadingBlock } from '@sf/ui';
import { RedirectIfAuthenticated, RequireAuth, useAuth } from '../auth';
import { IS_SHELL, SHELL_VIEWER_ONLY } from '../shell';

/**
 * All portal routes (ARCHITECTURE.md §5). Every page is code-split: `pnpm build` emits one chunk per
 * page, so the first paint only carries the shell.
 *
 * Adding a route = add the lazy import + a <Route> below, create the page under src/pages/** with a
 * default export, and add its i18n keys to src/locales/en.json.
 */
const AppLayout = lazy(() => import('./AppLayout'));
const AuthLayout = lazy(() => import('./AuthLayout'));

const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('../pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));

const TermsPage = lazy(() => import('../pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('../pages/legal/PrivacyPage'));

const HomePage = lazy(() => import('../pages/HomePage'));
const DeviceNewPage = lazy(() => import('../pages/devices/DeviceNewPage'));
const DeviceDetailPage = lazy(() => import('../pages/devices/DeviceDetailPage'));
const DeviceHistoryPage = lazy(() => import('../pages/devices/DeviceHistoryPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const AccountPage = lazy(() => import('../pages/account/AccountPage'));
const BillingPage = lazy(() => import('../pages/billing/BillingPage'));
const BillingReturnPage = lazy(() => import('../pages/billing/BillingReturnPage'));
const WithdrawPage = lazy(() => import('../pages/withdraw/WithdrawPage'));
const FeedbackPage = lazy(() => import('../pages/feedback/FeedbackPage'));
const ImpersonatePage = lazy(() => import('../pages/ImpersonatePage'));
const ActivatePage = lazy(() => import('../pages/auth/ActivatePage'));
const DemoPage = lazy(() => import('../pages/DemoPage'));
const SsoCallbackPage = lazy(() => import('../pages/sso/SsoCallbackPage'));
const SsoCompletePage = lazy(() => import('../pages/sso/SsoCompletePage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

/**
 * /terms and /privacy are reachable both ways: from an e-mail or the signed-out footer, and from the
 * Account hub inside the app. Signed out they keep the public frame. Signed in they have to keep the
 * app frame — on a phone, dropping the tab bar and showing a login-style brand lockup after tapping a
 * settings row reads as an accidental sign-out.
 */
function LegalLayout() {
  const { status } = useAuth();
  return status === 'authenticated' ? <AppLayout /> : <AuthLayout />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingBlock />}>
      <Routes>
        {/* anonymous */}
        <Route
          element={
            <RedirectIfAuthenticated>
              <AuthLayout />
            </RedirectIfAuthenticated>
          }
        >
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* reachable signed in or out (the link comes from an e-mail, or from the footer) */}
        <Route element={<AuthLayout />}>
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          {/* Admin handover: stores the short-lived customer token from the fragment, then goes home. */}
          <Route path="/impersonate" element={<ImpersonatePage />} />
          {/* The one-tap link in the two "monitoring is not active" mails. Inert in the store apps, which may
              show no way to buy: there it is the home signpost, and `activate` is deliberately absent from the
              deep-link allowlist, so eu.solmiro.app://activate is dropped whole. */}
          <Route
            path="/activate"
            element={IS_SHELL && SHELL_VIEWER_ONLY ? <Navigate to="/" replace /> : <ActivatePage />}
          />
          {/* "Try the demo": opens the shared read-only account for ?lng= and goes home. Public, and
              deliberately outside RedirectIfAuthenticated — whoever is signed in here is replaced by the
              demo, but only once the demo has actually opened. */}
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/sso/complete" element={<SsoCompletePage />} />
        </Route>

        {/* Anonymous or signed in; LegalLayout picks the frame. */}
        <Route element={<LegalLayout />}>
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Route>

        {/* signed in */}
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          {/* "/" is a signpost, not a page: no inverter → the wizard, otherwise the last one viewed. */}
          <Route index element={<HomePage />} />
          <Route path="/devices/new" element={<DeviceNewPage />} />
          <Route path="/devices/:id" element={<DeviceDetailPage />} />
          {/* The production charts, one tap below the overview. `?date=` and `?view=` deep-link a
              specific day or view — the overview stays the meaning of /devices/:id. */}
          <Route path="/devices/:id/history" element={<DeviceHistoryPage />} />
          <Route path="/devices" element={<Navigate to="/" replace />} />
          {/* The dashboard is gone; old links and e-mails must not 404. */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/account" element={<AccountPage />} />
          {/* The store apps are viewers (SHELL_VIEWER_ONLY): no billing page, the account hub shows the status. */}
          <Route
            path="/billing"
            element={IS_SHELL && SHELL_VIEWER_ONLY ? <Navigate to="/account" replace /> : <BillingPage />}
          />
          <Route
            path="/billing/return"
            element={
              IS_SHELL && SHELL_VIEWER_ONLY ? <Navigate to="/account" replace /> : <BillingReturnPage />
            }
          />
          {/* art. 6:230oa BW — its own route so the entry point can be a plain, permanent link. */}
          <Route path="/withdraw" element={<WithdrawPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
